import * as PIXI from 'pixi.js';
import { Scale } from '../multigame/MyHelpers';

 interface ContainerDebugParams
{
    color?: PIXI.ColorSource;
    secondaryColor?: PIXI.ColorSource;
    name?: string;
}


 class FixedSizeContainer extends PIXI.Container
{

    public fixedWidth: number;
    public fixedHeight: number;
    public debugParams: ContainerDebugParams;
    public debugGraphics: PIXI.Graphics = null;

    public constructor(width: number, height: number, debugParams?: ContainerDebugParams)
    {
        super();
        this.fixedWidth = width;
        this.fixedHeight = height;
        this.debugParams = debugParams;
        this.name = debugParams?.name;

        if (this.debugParams?.color)
        {
            this.debugGraphics = new PIXI.Graphics();
            this.debugGraphics.name = this.debugParams?.name ?? "debugGraphics";

            this.debugGraphics.beginFill(this.debugParams?.color ?? 'red');
            this.debugGraphics.drawRect(0, 0, this.fixedWidth, this.fixedHeight);
            this.debugGraphics.endFill();

            this.addChild(this.debugGraphics);
        }
    }


    public prevParentWidth: number;
    public prevParentHeight: number;

    public calculatedWidth: number;
    public calculatedHeight: number;

    override updateTransform(): void
    {



        if (this.parent)
        {
            let parentWidth: number;
            let parentHeight: number;

            if (this.parent instanceof ResponsiveView)
            {
                parentWidth = this.parent.availableWidth;
                parentHeight = this.parent.availableHeight;
            }
            else if (this.parent instanceof FixedSizeContainer)
            {
                parentWidth = this.parent.calculatedWidth;
                parentHeight = this.parent.calculatedHeight;
            }
            else if (this.parent instanceof ResponsiveSizeContainer)
            {
                parentWidth = this.parent.calculatedWidth;
                parentHeight = this.parent.calculatedHeight;
            }

            const parentSizeChanged = this.prevParentWidth != parentWidth || this.prevParentHeight != parentHeight;
            if (parentSizeChanged)
            {
                console.log(`FixedSizeContainer() ${this.name} parentWidth: ${parentWidth}, parentHeight: ${parentHeight}`);
                
                const scalex = parentWidth / this.fixedWidth;
                const scaley = parentHeight / this.fixedHeight;

                const minscale = Math.min(scalex, scaley);

                console.log(`FixedSizeContainer() ${this.name} scalex: ${scalex}, scaley: ${scaley}, minscale: ${minscale}`);

                // center position relative to parent
                this.calculatedWidth = Math.round(this.fixedWidth * minscale);
                this.calculatedHeight = Math.round(this.fixedHeight * minscale);

                this.position.set(parentWidth / 2 - this.calculatedWidth / 2, parentHeight / 2 - this.calculatedHeight / 2);

                this.scale.set(minscale, minscale);

                this.prevParentWidth = parentWidth;
                this.prevParentHeight = parentHeight;
            }
        }

        super.updateTransform();
    }
}

 class ResponsiveSizeContainer extends PIXI.Container
{
    
    public percentX: number;
    public percentY: number;
    public percentWidth: number;
    public percentHeight: number;

    public maxRatio: number;

    public debugParams: ContainerDebugParams;


    public calculatedX: number;
    public calculatedY: number;
    public calculatedWidth: number;
    public calculatedHeight: number;

    public debugGraphics: PIXI.Graphics = null;

    public constructor(percentX: number, percentY: number, percentWidth: number, percentHeight: number, maxRatio: number = NaN, debugParams?: ContainerDebugParams)
    {
        super();
        this.percentX = percentX;
        this.percentY = percentY;
        this.percentWidth = percentWidth;
        this.percentHeight = percentHeight;
        this.maxRatio = maxRatio;
        this.debugParams = debugParams;
        this.name = debugParams?.name;

        if (this.debugParams?.color)
        {
            this.debugGraphics = new PIXI.Graphics();
            this.debugGraphics.name = this.debugParams?.name ?? "debugGraphics";
            this.addChild(this.debugGraphics);
        }
    }

    protected updateDebugGraphics(): void
    {
        if (!this.debugGraphics)
        {
            // no debug graphics
            return;
        }

        this.debugGraphics.clear();
        this.debugGraphics.beginFill(this.debugParams?.color ?? 'red');
        this.debugGraphics.drawRect(0, 0, this.calculatedWidth, this.calculatedHeight);
        this.debugGraphics.endFill();
    }

    override updateTransform(): void
    {
        let parentWidth: number;
        let parentHeight: number;

        if (this.parent)
        {
            if (this.parent instanceof ResponsiveView)
            {
                parentWidth = this.parent.availableWidth;
                parentHeight = this.parent.availableHeight;
            }
            else if (this.parent instanceof FixedSizeContainer)
            {
                parentWidth = this.parent.fixedWidth;
                parentHeight = this.parent.fixedHeight;
            }
            else if (this.parent instanceof ResponsiveSizeContainer)
            {
                parentWidth = this.parent.calculatedWidth;
                parentHeight = this.parent.calculatedHeight;
            }

            let newX = Math.round(parentWidth * this.percentX);
            let newY = Math.round(parentHeight * this.percentY);

            let newCalculatedWidth = Math.round(parentWidth * this.percentWidth);
            let newCalculatedHeight = Math.round(parentHeight * this.percentHeight);

            let naturalRatio = newCalculatedWidth / newCalculatedHeight;

            if (!isNaN(this.maxRatio))
            {
                if (naturalRatio > this.maxRatio)
                {

                    // limit new calculated size to the max ratio
                    let limitedWidth = Math.round(newCalculatedHeight * this.maxRatio);
                    newX = (newCalculatedWidth - limitedWidth) / 2;
                    newCalculatedWidth = limitedWidth;
                }
            }
            const ratio = newCalculatedWidth / newCalculatedHeight;

            const sizeChanged = this.calculatedWidth != newCalculatedWidth || this.calculatedHeight != newCalculatedHeight;

            // if new size is different
            if (sizeChanged)
            {
                console.log(`ResponsiveSizeContainer.updateTransform() ${this.name} (percent size: ${this.percentX}x${this.percentY} ${this.percentWidth}x${this.percentHeight}) parent size: ${parentWidth}x${parentHeight}, calculated +${this.calculatedX}+${this.calculatedY} ${this.calculatedWidth}x${this.calculatedHeight}, naturalRatio ${naturalRatio} ratio ${ratio} maxratio ${this.maxRatio}`); 
            }

            this.updateDebugGraphics();


            this.calculatedWidth = newCalculatedWidth;
            this.calculatedHeight = newCalculatedHeight;
            this.calculatedX = newX;
            this.calculatedY = newY;

            this.position.set(this.calculatedX, this.calculatedY);
        }

        super.updateTransform();
    }
}

 class ResponsiveView extends PIXI.Container
{
    public debugParams: ContainerDebugParams;
    public availableWidth: number;
    public availableHeight: number;

    public constructor(startingWidth: number, startingHeight: number, debugParams?: ContainerDebugParams)
    {
        super();

        this.availableWidth = startingWidth;
        this.availableHeight = startingHeight;
        this.debugParams = debugParams;
        this.name = debugParams?.name;

        console.log(`ResponsiveView.constructor() ${this.name} startingWidth: ${startingWidth}, startingHeight: ${startingHeight}`);
    }

    public setAvailableSize(availableWidth: number, availableHeight: number)
    {
        console.log(`ResponsiveView.setAvailableSize() ${this.name} availableWidth: ${availableWidth}, availableHeight: ${availableHeight}`);
        this.availableWidth = availableWidth;
        this.availableHeight = availableHeight;
    }
}

 interface IView
{
    Create(): void;
    Destroy(): void;
}

 interface IPortraitView extends IView
{
    
}

 interface ILandscapeView extends IView
{
    
}

enum MyOrientation
{
    Portrait = "Portrait",
    Landscape = "Landscape"
}

 class ResponsiveOrientationSwitchingContainer extends ResponsiveSizeContainer
{
    protected switchRatio: number;

    protected portraitView: IPortraitView;
    protected landscapeView: ILandscapeView;

    protected currentOrientation: MyOrientation = MyOrientation.Portrait;
    protected currentView: IView;

    public constructor(switchRatio: number, percentX: number, percentY: number, percentWidth: number, percentHeight: number, maxRatio: number = NaN, debugParams?: ContainerDebugParams)
	{
		super(percentX, percentY, percentWidth, percentHeight, maxRatio, debugParams);

        this.switchRatio = switchRatio;
	}

    public SetViews(portraitView: IPortraitView, landscapeView: ILandscapeView)
    {
        this.portraitView = portraitView;
        this.landscapeView = landscapeView;
        
        this.currentOrientation = MyOrientation.Portrait;
        this.currentView = portraitView;
        this.currentView.Create();
    }

    protected override updateDebugGraphics(): void
    {
        if (!this.debugGraphics)
        {
            // no debug graphics
            return;
        }

        let color: PIXI.ColorSource = "0x00000000";
        if (this.currentOrientation == MyOrientation.Portrait)
        {
            color = this.debugParams?.color ?? color;
        }
        else if (this.currentOrientation == MyOrientation.Landscape)
        {
            color = this.debugParams?.secondaryColor ?? color;
        }
        
        this.debugGraphics.clear();
        this.debugGraphics.beginFill(color);
        this.debugGraphics.drawRect(0, 0, this.calculatedWidth, this.calculatedHeight);
        this.debugGraphics.endFill();
    }

    protected SwitchOrientation(orientation: MyOrientation): void
    {
        console.log(`ResponsiveOrientationSwitchingContainer.SwitchOrientation() orientation: ${orientation}`);
        this.currentOrientation = orientation;
    }

    public override updateTransform(): void
    {
        super.updateTransform();

        const ratio = this.calculatedWidth / this.calculatedHeight;

        if (ratio >= this.switchRatio && this.currentOrientation == MyOrientation.Portrait)
        {
            this.SwitchOrientation(MyOrientation.Landscape);
        }
        else if (ratio < this.switchRatio && this.currentOrientation == MyOrientation.Landscape)
        {
            this.SwitchOrientation(MyOrientation.Portrait);
        }
    }
}

 class FixedOrientationSwitchingContainer extends FixedSizeContainer
{

    protected switchRatio: number;
    protected currentOrientation: MyOrientation = MyOrientation.Portrait;


    public constructor(switchRatio:number, width: number, height: number, debugParams?: ContainerDebugParams)
    {
        super(width, height, debugParams);
        this.switchRatio = switchRatio;
    }

    protected SwitchOrientation(orientation: MyOrientation): void
    {
        console.log(`FixedOrientationSwitchingContainer.SwitchOrientation() orientation: ${orientation}`);
        this.currentOrientation = orientation;
    }

    public override updateTransform(): void
    {
        super.updateTransform();



        if (this.parent)
        {
            let parentWidth: number;
            let parentHeight: number;

            if (this.parent instanceof ResponsiveView)
            {
                parentWidth = this.parent.availableWidth;
                parentHeight = this.parent.availableHeight;
            }
            else if (this.parent instanceof FixedSizeContainer)
            {
                parentWidth = this.parent.calculatedWidth;
                parentHeight = this.parent.calculatedHeight;
            }
            else if (this.parent instanceof ResponsiveSizeContainer)
            {
                parentWidth = this.parent.calculatedWidth;
                parentHeight = this.parent.calculatedHeight;
            }

            const ratio = parentWidth / parentHeight;

            console.log(`FixedOrientationSwitchingContainer.updateTransform() parentWidth: ${parentWidth}, parentHeight: ${parentHeight}, ratio: ${ratio}`);

            if (ratio >= this.switchRatio && this.currentOrientation == MyOrientation.Portrait)
            {
                this.SwitchOrientation(MyOrientation.Landscape);
            }
            else if (ratio < this.switchRatio && this.currentOrientation == MyOrientation.Landscape)
            {
                this.SwitchOrientation(MyOrientation.Portrait);
            }
        }
    }
}

 class JackpotView extends ResponsiveOrientationSwitchingContainer
{
    jp4container: ResponsiveSizeContainer;
    jp3container: ResponsiveSizeContainer;
    jp2container: ResponsiveSizeContainer;
    jp1container: ResponsiveSizeContainer;

    logoContainer: ResponsiveSizeContainer;

    public constructor(switchRatio: number, x: number, y: number, width: number, height: number, maxRatio: number = NaN, debugParams?: ContainerDebugParams)
	{
		super(switchRatio, x, y, width, height, maxRatio, debugParams);

        this.CreateLandscape();
    }

    override SwitchOrientation(orientation: MyOrientation): void
    {
        super.SwitchOrientation(orientation);

        if (orientation == MyOrientation.Portrait)
        {
            this.DestroyLandscape();
            this.CreatePortrait();
        }
        else
        {
            this.DestroyPortrait();
            this.CreateLandscape();
        }
    }
    
    CreateLandscape(): void
    {
        const margin = 0.01;
        this.jp4container = new ResponsiveSizeContainer(0.00 + margin, 0.00 + margin, 0.50 - 2*margin, 0.60 - 2*margin, NaN, {color: 0x000099, name: 'jp4-container'});  
        this.addChild(this.jp4container);

        this.jp3container = new ResponsiveSizeContainer(0.50 + margin, 0.00 + margin, 0.50 - 2*margin, 0.60 - 2*margin, NaN,{color: 0x000099, name: 'jp3-container'});
        this.addChild(this.jp3container);

        this.jp2container = new ResponsiveSizeContainer(0.10 + margin, 0.60 + margin, 0.30 - 2*margin, 0.40 - 2*margin, NaN,{color: 0x000099, name: 'jp2-container'});  
        this.addChild(this.jp2container);

        this.jp1container = new ResponsiveSizeContainer(0.60 + margin, 0.60 + margin, 0.30 - 2*margin, 0.40 - 2*margin, NaN,{color: 0x000099, name: 'jp1-container'});
        this.addChild(this.jp1container);
    }

    DestroyLandscape(): void
	{
		this.jp4container.destroy();
		this.jp3container.destroy();
		this.jp2container.destroy();
		this.jp1container.destroy();
        this.logoContainer?.destroy();
	}

    CreatePortrait(): void
    {
        const margin = 0.01;
        this.jp4container = new ResponsiveSizeContainer(0.00 + margin, 0.00 + margin, 1 - 2*margin, 0.33 - 2*margin, NaN, {color: 0x000099, name: 'jp4-container'});
        this.addChild(this.jp4container);

        this.jp3container = new ResponsiveSizeContainer(0.05 + margin, 0.33 + margin, 0.9 - 2*margin, 0.33 - 2*margin, NaN, {color: 0x000099, name: 'jp3-container'});
        this.addChild(this.jp3container);

        this.jp2container = new ResponsiveSizeContainer(0.05 + margin, 0.66 + margin, 0.25 - 2*margin, 0.33 - 2*margin, NaN, {color: 0x000099, name: 'jp2-container'});  
        this.addChild(this.jp2container);

        this.logoContainer = new ResponsiveSizeContainer(0.30 + margin, 0.66 + margin, 0.40 - 2*margin, 0.33 - 2*margin, NaN, {color: 0x000099, name: 'logo-container'});
        this.addChild(this.logoContainer);

        this.jp1container = new ResponsiveSizeContainer(0.70 + margin, 0.66 + margin, 0.25 - 2*margin, 0.33 - 2*margin, NaN, {color: 0x000099, name: 'jp1-container'});
        this.addChild(this.jp1container);
    }

    DestroyPortrait(): void
	{
        this.jp4container.destroy();
		this.jp3container.destroy();
		this.jp2container.destroy();
		this.jp1container.destroy();
        this.logoContainer?.destroy();
	}
}
export class MockUpPixiApp_Responsive
{
    protected app: PIXI.Application<HTMLCanvasElement>;

    protected responsiveView: ResponsiveView;
    protected jackpotContainer: PIXI.Container;
    protected jackpotView: JackpotView;
    protected baseGameContainer: PIXI.Container;

    protected reelsContainer: FixedSizeContainer;

    protected panelContainer: PIXI.Container;


    constructor()
    {
        if (!PIXI.utils.isWebGLSupported())
        {
            throw new Error('WebGL is not supported');
        }

        this.app = new PIXI.Application<HTMLCanvasElement>({
            backgroundColor: 0x1099bb,
            width: window.innerWidth,
            height: window.innerHeight,
            //resizeTo: window,
            //resolution: 1
            // resolution: 1,
            //autoDensity: true,

        })
        document.body.appendChild(this.app.view);
        this.app.stage.name = 'Responsive-pixi-app';

        globalThis.__PIXI_APP__ = this.app;

        this.CreateContainers();
        
        // this.resize();

        window.addEventListener('resize', () => {
            this.app.renderer.resize(window.innerWidth, window.innerHeight);
            this.responsiveView.setAvailableSize(window.innerWidth, window.innerHeight)
        });
        this.responsiveView.setAvailableSize(window.innerWidth, window.innerHeight);
    }

    protected CreateContainers()
    {
        this.responsiveView = new ResponsiveView(window.screen.availWidth, window.screen.availHeight, {color: 0x220000, name: 'responsive-view'});  

        this.jackpotContainer = new ResponsiveSizeContainer(0, 0, 1, 0.25, NaN,{color: 0x550000, secondaryColor: 0x005500, name: 'jackpot-container'});
        this.responsiveView.addChild(this.jackpotContainer);

        this.jackpotView = new JackpotView(2.5, 0, 0, 1, 1, 6, {color: 0x550000, secondaryColor: 0x005500, name: 'jackpot-view'});
        this.jackpotContainer.addChild(this.jackpotView);

        this.baseGameContainer = new ResponsiveSizeContainer(0, 0.25 , 1, 0.6, NaN, {color: 0x990000, name: 'base-game-container'});
        this.responsiveView.addChild(this.baseGameContainer);

        this.reelsContainer = new FixedSizeContainer(Scale.W(1920), Scale.H(1080), {color: 0xbb0000, name: 'reels-container'});
        this.baseGameContainer.addChild(this.reelsContainer);


        this.panelContainer = new ResponsiveSizeContainer(0, 0.85, 1, 0.15, NaN, {color: 0xee0000, name: 'panel-container'});
        this.responsiveView.addChild(this.panelContainer);

        // this.responsiveView.addChild(this.jackpotContainer, this.panelContainer, this.baseGameContainer);
        this.app.stage.addChild(this.responsiveView);
        this.app.render();
    }
}